import React from 'react';
import * as THREE from 'three';
import { PerpendicularArrow } from './PerpendicularArrow';
import { CircularMomentArrow } from './CircularMomentArrow';
import { scaleToGrid } from '@/utils/gridScale';

interface Props {
  nodes: Array<{ id: string; x: number; y: number; z: number }>;
  nodeMomentLoads: Record<string, { x: number; y: number; z: number }>;
  nodePerpLoads: Record<string, { x: number; y: number; z: number }>;
  scaleFactor?: number;
}

export const NodeLoadIndicators: React.FC<Props> = ({ nodes, nodeMomentLoads, nodePerpLoads, scaleFactor = 1 }) => {
  // Merge both maps into renderable groups so both types can display at the same node
  const nodeIds = Array.from(new Set([...Object.keys(nodeMomentLoads || {}), ...Object.keys(nodePerpLoads || {})]));
  
  // Normalize visual scale: cap at 1 to prevent oversized indicators at small scales (0-10)
  // At scale 50-100 (scaleFactor=0.1): visualScale = 1 (base size)
  // At scale 0-10 (scaleFactor=1.0): visualScale = 1 (capped, not 10x)
  // At scale 100-500 (scaleFactor=0.02): visualScale = 0.2 (proportionally smaller)
  const visualScale = Math.min(scaleFactor * 10, 1);

  return (
    <>
      {nodeIds.map((nodeId) => {
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) return null;

        // Scale node positions
        const position = new THREE.Vector3(
          scaleToGrid(node.x, scaleFactor),
          scaleToGrid(node.y, scaleFactor),
          scaleToGrid(node.z, scaleFactor)
        );
        const m = nodeMomentLoads && nodeMomentLoads[nodeId];
        const p = nodePerpLoads && nodePerpLoads[nodeId];

        return (
          <group key={`nload-${nodeId}`} position={position}>
            {/* Type 1 - Moments (circular) */}
            {m && (m.x !== 0 || m.y !== 0 || m.z !== 0) && (
              <group scale={[visualScale, visualScale, visualScale]}>
                {m.x !== 0 && <CircularMomentArrow position={new THREE.Vector3(0, 0, 0)} axis={'X'} value={m.x} color={'#ff6b6b'} />}
                {m.y !== 0 && <CircularMomentArrow position={new THREE.Vector3(0, 0, 0)} axis={'Y'} value={m.y} color={'#ff6b6b'} />}
                {m.z !== 0 && <CircularMomentArrow position={new THREE.Vector3(0, 0, 0)} axis={'Z'} value={m.z} color={'#ff6b6b'} />}
              </group>
            )}

            {/* Type 2 - Perpendicular arrows ✅ NOW PASSES VALUE */}
            {p && (p.x !== 0 || p.y !== 0 || p.z !== 0) && (
              <group>
                {p.x !== 0 && (
                  <PerpendicularArrow
                    position={new THREE.Vector3(0, 0, 0)}
                    plane={"X"}
                    value={p.x}
                  />
                )}
                {p.y !== 0 && (
                  <PerpendicularArrow
                    position={new THREE.Vector3(0, 0, 0)}
                    plane={"Y"}
                    value={p.y}
                  />
                )}
                {p.z !== 0 && (
                  <PerpendicularArrow
                    position={new THREE.Vector3(0, 0, 0)}
                    plane={"Z"}
                    value={p.z}
                  />
                )}
              </group>
            )}
          </group>
        );
      })}
    </>
  );
};

export default NodeLoadIndicators;
